import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ObserverNav from '../components/ObserverNav';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { UserPlus, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  parent_name: '', email: '', phone: '',
  child_name: '', child_age: '', school_name: '', message: '',
};

export default function ObserverAddInquiry() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('observer_token')) navigate('/observer/login');
  }, [navigate]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const { parent_name, email, phone, child_name, child_age } = form;
    if (!parent_name.trim() || !email.trim() || !phone.trim() || !child_name.trim() || !child_age) {
      toast({ title: 'Missing details', description: 'Parent name, email, phone, child name and age are required.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('observer_token');
      await axios.post(`${BACKEND_URL}/api/observer/inquiries`, {
        token,
        parent_name: parent_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        child_name: child_name.trim(),
        child_age: parseInt(child_age, 10) || 0,
        school_name: form.school_name.trim(),
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast({ title: 'Inquiry submitted', description: 'It now shows up in Admin → Inquiries.' });
    } catch (err) {
      toast({
        title: 'Could not submit inquiry',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-violet-600" />
            Refer a Family
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Know a family who could benefit from Sanjaya? Submit their details here — it lands in
            Admin&rsquo;s Inquiries list, tagged with your name, for follow-up and enrollment.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Family Details</CardTitle>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Inquiry submitted</h3>
                <p className="text-gray-600 text-sm mt-1">Our admin team will follow up with this family.</p>
                <Button
                  variant="outline"
                  className="mt-5"
                  onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); }}
                  data-testid="observer-inquiry-add-another-btn"
                >
                  Refer another family
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="observer-inquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Parent Name *</Label>
                    <Input value={form.parent_name} onChange={set('parent_name')} placeholder="Parent's full name" className="mt-1" data-testid="observer-inquiry-parent-name" />
                  </div>
                  <div>
                    <Label>Child&rsquo;s Name *</Label>
                    <Input value={form.child_name} onChange={set('child_name')} placeholder="Child's full name" className="mt-1" data-testid="observer-inquiry-child-name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Parent Email *</Label>
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="parent@email.com" className="mt-1" data-testid="observer-inquiry-email" />
                  </div>
                  <div>
                    <Label>Parent Phone *</Label>
                    <Input value={form.phone} onChange={set('phone')} placeholder="+91" className="mt-1" data-testid="observer-inquiry-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Child&rsquo;s Age *</Label>
                    <Input type="number" min="1" max="18" value={form.child_age} onChange={set('child_age')} placeholder="e.g. 9" className="mt-1" data-testid="observer-inquiry-child-age" />
                  </div>
                  <div>
                    <Label>School</Label>
                    <Input value={form.school_name} onChange={set('school_name')} placeholder="School name" className="mt-1" data-testid="observer-inquiry-school" />
                  </div>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea value={form.message} onChange={set('message')} placeholder="Anything Admin should know before reaching out..." className="mt-1" data-testid="observer-inquiry-message" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full gap-2 bg-violet-600 hover:bg-violet-700 text-white" data-testid="observer-inquiry-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Submit Inquiry
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
